import { API_USER_URL } from "../hook/api";

export type AssignmentKind = "pdf" | "html" | "image";

export type AssignmentPayload = {
    kind: AssignmentKind;
    content: ArrayBuffer | string;
    imageUrl?: string;
};

type CacheEntry = {
    payload: AssignmentPayload;
    /** Blob URL for images; revoked when the cache entry is replaced. */
    imageUrl?: string;
    at: number;
};

const CACHE_TTL_MS = 10 * 60 * 1000;
const FETCH_TIMEOUT_MS = 20000;
const cache = new Map<number, CacheEntry>();
const inflight = new Map<number, Promise<AssignmentPayload>>();

function htmlEncoding(bytes: Uint8Array): string {
    if (bytes[0] === 0xff && bytes[1] === 0xfe) return "utf-16le";
    if (bytes[0] === 0xfe && bytes[1] === 0xff) return "utf-16be";
    const head = new TextDecoder("latin1").decode(bytes.subarray(0, 2048));
    const match = head.match(/<meta[^>]+charset\s*=\s*["']?\s*([\w-]+)/i);
    const label = match?.[1]?.toLowerCase();
    if (label) {
        try {
            new TextDecoder(label);
            return label;
        } catch {
            /* unknown label */
        }
    }
    return "utf-8";
}

function kindFromType(contentType: string): AssignmentKind | null {
    const type = contentType.toLowerCase();
    if (type.includes("pdf")) return "pdf";
    if (type.includes("html")) return "html";
    if (type.startsWith("image/")) return "image";
    return null;
}

function readCache(programId: number): AssignmentPayload | null {
    const entry = cache.get(programId);
    if (!entry) return null;
    if (Date.now() - entry.at > CACHE_TTL_MS) {
        if (entry.imageUrl) URL.revokeObjectURL(entry.imageUrl);
        cache.delete(programId);
        return null;
    }
    return entry.payload;
}

function writeCache(programId: number, payload: AssignmentPayload, imageUrl?: string) {
    const previous = cache.get(programId);
    if (previous?.imageUrl && previous.imageUrl !== imageUrl) {
        URL.revokeObjectURL(previous.imageUrl);
    }
    cache.set(programId, { payload, imageUrl, at: Date.now() });
}

async function fetchAssignment(programId: number, signal?: AbortSignal): Promise<AssignmentPayload> {
    const token = localStorage.getItem("ryd-parent-token");
    const controller = new AbortController();
    const onAbort = () => controller.abort();
    signal?.addEventListener("abort", onAbort);
    const timer = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
        const res = await fetch(`${API_USER_URL}/parent/program/${encodeURIComponent(String(programId))}/assignment`, {
            headers: { authorization: `bearer ${token}` },
            cache: "force-cache",
            signal: controller.signal,
        });
        if (res.status === 401 || res.status === 403) {
            localStorage.clear();
            window.location.href = "/parent/sign-in";
            throw new Error("Session expired");
        }
        if (!res.ok) {
            let message = "Could not open this assignment";
            try {
                message = (await res.json())?.message || message;
            } catch {
                /* keep default */
            }
            throw new Error(message);
        }
        const kind = kindFromType(res.headers.get("content-type") || "");
        if (!kind) throw new Error("This assignment can only be viewed when it is a PDF, HTML, or image");
        const buffer = await res.arrayBuffer();
        if (kind === "pdf") {
            const payload = { kind, content: buffer };
            writeCache(programId, payload);
            return payload;
        }
        if (kind === "html") {
            const bytes = new Uint8Array(buffer);
            const payload = { kind, content: new TextDecoder(htmlEncoding(bytes)).decode(bytes) };
            writeCache(programId, payload);
            return payload;
        }
        const imageUrl = URL.createObjectURL(new Blob([buffer], { type: res.headers.get("content-type") || "image/jpeg" }));
        const payload = { kind, content: buffer, imageUrl };
        writeCache(programId, payload, imageUrl);
        return payload;
    } catch (error: any) {
        if (error?.name === "AbortError") {
            throw new Error("The assignment is taking too long to load. Please try again.");
        }
        throw error instanceof Error ? error : new Error(String(error?.message || "Could not open this assignment"));
    } finally {
        window.clearTimeout(timer);
        signal?.removeEventListener("abort", onAbort);
    }
}

/** Returns a cached assignment immediately when available. */
export function getCachedAssignment(programId: number): AssignmentPayload | null {
    return readCache(programId);
}

/** Drop a cached assignment so the next open fetches a fresh copy. */
export function clearAssignmentCache(programId?: number) {
    if (programId == null) {
        Array.from(cache.values()).forEach((entry) => {
            if (entry.imageUrl) URL.revokeObjectURL(entry.imageUrl);
        });
        cache.clear();
        return;
    }
    const entry = cache.get(programId);
    if (entry?.imageUrl) URL.revokeObjectURL(entry.imageUrl);
    cache.delete(programId);
}

/**
 * Loads an assignment for the in-app viewer. Reuses an in-flight request and
 * keeps a short memory cache so reopening feels instant.
 */
export function loadAssignment(programId: number, signal?: AbortSignal, force = false): Promise<AssignmentPayload> {
    if (force) clearAssignmentCache(programId);
    const cached = force ? null : readCache(programId);
    if (cached) return Promise.resolve(cached);

    const existing = inflight.get(programId);
    if (existing) {
        return existing.then((payload) => {
            if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
            return payload;
        });
    }

    const request = fetchAssignment(programId)
        .finally(() => {
            if (inflight.get(programId) === request) inflight.delete(programId);
        });
    inflight.set(programId, request);
    return request.then((payload) => {
        if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
        return payload;
    });
}

/** Start loading in the background (e.g. on hover) so the viewer opens faster. */
export function prefetchAssignment(programId: number) {
    if (!programId || readCache(programId) || inflight.has(programId)) return;
    loadAssignment(programId).catch(() => undefined);
}
